import type { ReactNode } from 'react';
import { CandidateTopper, type CandidateTopperStyle } from '@tempmee/react-blocks';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import type { IJob, IUser } from '~/models';
import { resolveProfession } from '~/utils/shiftCard';
import { BookedProfessionalDetails } from './BookedProfessionalDetails';
import { ShiftDetailsPanel } from './ShiftDetailsPanel';
import { toBookedProfessionalCardData } from './shared/utils/bookedProfessionalProps';

export interface IBookedProfessionalPanelProps {
  /**
   * Nullable because the cancelled page composes this panel from a job that may
   * still be loading. The scheduled and completed pages hand over a job they
   * already have; they simply lose the compiler's word for it here.
   */
  job: Partial<IJob> | null | undefined;
  /**
   * The professional's full profile, once it has loaded.
   *
   * Separate from `job.offer_final_reciever` on purpose: the shift payload names
   * the professional but carries none of their education, so the profile has to
   * be fetched. Until it lands this is `null` and only the topper renders.
   */
  professional: IUser | null;
  /** Verified credentials for the Credentials tab. */
  authorizations?: IAuthorization[] | null;
  /**
   * Which Figma topper frame this page is. Only the professional styles belong
   * here — `cancelled-unfilled` has no professional, so it is a different panel.
   */
  topperStyle: Exclude<CandidateTopperStyle, 'cancelled-unfilled'>;
  /**
   * The page's buttons. Sit top-right at desktop width and stack into their own
   * full-width row below `@md` — which is why they are a different slot from
   * {@link overflowMenu}, and why only the page can decide which it is handing over.
   */
  actions?: ReactNode[];
  /** The page's `⋯` menu. Pinned to the corner at every width, never stacked. */
  overflowMenu?: ReactNode;
  /**
   * Prefix for this panel's test ids, e.g. `scheduled` yields
   * `scheduled-professional-panel` and `scheduled-professional-topper`. Each page
   * keeps the selectors its own tests and E2E specs already use.
   */
  testIDPrefix: string;
  /**
   * Whether the shell has stacked its columns.
   *
   * Only `CandidateDetails` still needs telling: it has no container query, so the
   * two places the design restructures rather than reflows (the summary row's
   * divider direction and the badge size) are a prop. The topper answers to its
   * own width.
   */
  isCollapsed?: boolean;
  /**
   * What sits under the topper, when it is not the professional's profile.
   *
   * The cancelled page swaps the profile for a reposted notice when the
   * professional is the one who walked away — same shell, different body, which
   * is why that page composes this panel rather than rebuilding it.
   */
  children?: ReactNode;
  /** Extra classes for the panel surface, e.g. a min-height floor. */
  className?: string;
}

/**
 * The primary column of every Shift Details page that has one professional to
 * show: who holds the shift, and their profile beneath.
 *
 * One component for the scheduled, completed and cancelled frames because the
 * Figma draws one card for all three. They differ in the topper's `style` and in
 * which slot the page's controls go into — a scheduled shift offers *Message
 * Professional*, a button; the terminal ones offer a `⋯` menu — and the block
 * itself owns that distinction, so there is nothing left for three separate
 * panels to disagree about.
 *
 * No rule between the topper and the profile, on any of the three: the cancelled
 * page tried one and reverted it, and neither the completed nor the scheduled
 * frame draws one either — so the card stays on `ShiftDetailsPanel`'s own uniform
 * padding and gap rather than one state carrying a divider the others do not.
 *
 * The identity comes off the shift and the profile off the fetched professional,
 * which is why the topper renders immediately and the details arrive after: the
 * shift names who is booked, but not what they are qualified in.
 */
export const BookedProfessionalPanel = ({
  job,
  professional,
  authorizations,
  topperStyle,
  actions,
  overflowMenu,
  testIDPrefix,
  isCollapsed = false,
  children,
  className,
}: IBookedProfessionalPanelProps) => {
  /*
   * The topper's identity is available from the shift alone, so it is fed from
   * `offer_final_reciever` and upgraded to the fetched profile once that lands —
   * which is what fills in the badges and the rating the shift payload omits.
   */
  const topperSource = professional ?? job?.offer_final_reciever;

  return (
    <ShiftDetailsPanel
      className={className}
      data-testid={`${testIDPrefix}-professional-panel`}
      data-cy={`${testIDPrefix}-professional-panel`}
    >
      <CandidateTopper
        topperStyle={topperStyle}
        {...toBookedProfessionalCardData(topperSource)}
        roleCode={resolveProfession(job?.professionName, job?.subProfession)}
        actions={actions}
        overflowMenu={overflowMenu}
        testID={`${testIDPrefix}-professional-topper`}
      />

      {/*
        `undefined` means "no body given, draw the default"; `null` means "draw
        nothing", which is how the cancelled page shows a topper on its own. `??`
        would collapse the two and put the profile back under that topper.
      */}
      {children !== undefined ? (
        children
      ) : professional ? (
        <BookedProfessionalDetails
          job={job}
          professional={professional}
          authorizations={authorizations}
          isCollapsed={isCollapsed}
        />
      ) : null}
    </ShiftDetailsPanel>
  );
};
